import type { CataloguePlan } from '@/features/billing/api/billing';
import { billingCycleAdverb, billingIntervalsOf, perMonthMinor } from '@/features/billing/lib/features';

/**
 * How a plan is quoted on a marketing page.
 *
 * The pricing page and the home page's teaser both sell the same ladder, so
 * they round the same way, convert to a monthly figure the same way and badge
 * the same plan. Two marketing surfaces quoting one plan at two prices is the
 * failure this module exists to make impossible.
 *
 * Nothing here decides what a plan costs. Every figure comes from the
 * catalogue the platform owner publishes; this is only the presentation.
 */

/**
 * Marketing prices are whole rupees - a plan is ₹1,499, never ₹1,499.00. The
 * rounding is display only; the paise the owner set are what gets charged.
 */
export function formatPlanPrice(minor: number): string {
  return `₹${Math.round(minor / 100).toLocaleString('en-IN')}`;
}

/**
 * What a plan works out at per month, and what is actually charged.
 *
 * Every plan is quoted per month, whatever it bills on, because that is the
 * only figure a visitor can compare across the three cadences - a quarterly
 * price beside a monthly one reads as four times the product. The cycle price
 * is never hidden for it: it is printed beside the monthly figure, because
 * ₹1,695 a month is a promise about a ₹5,085 charge and the charge is the part
 * that leaves their account.
 *
 * `null` where there is nothing to convert - a free plan, or a plan that
 * already bills monthly, which is its own per-month price.
 */
export function perMonth(plan: CataloguePlan): { price: string; billed: string } | null {
  const monthly =
    plan.price_minor === 0
      ? null
      : perMonthMinor(plan.price_minor, plan.billing_interval, plan.billing_interval_count);
  if (monthly === null) return null;
  return { price: formatPlanPrice(monthly), billed: formatPlanPrice(plan.price_minor) };
}

/** "Billed quarterly", "Billed yearly", "Billed every 6 months". */
export function billedPhrase(plan: CataloguePlan): string {
  return `Billed ${billingCycleAdverb(plan.billing_interval, plan.billing_interval_count)}`;
}

/**
 * Which plan wears the badge.
 *
 * "Most popular" is marketing chrome with no column in the plans table, so it
 * is derived rather than invented: the middle paid plan, which is the one a
 * three-tier ladder exists to sell. With fewer than three paid plans there is
 * no middle to point at, and nothing is badged.
 */
/** Plans a visitor can check out on: everything but the sales-led ones. */
export function selfServePlans(plans: CataloguePlan[]): CataloguePlan[] {
  return plans.filter((plan) => !plan.contact_sales);
}

/**
 * The cards for one billing-cycle tab: the self-serve plans sold on that
 * cycle, then every sales-led plan. Enterprise has no cycle of its own to
 * belong to, and a tab without it would read as "not offered on yearly".
 * Each half keeps the owner's sort order.
 */
export function plansForInterval(plans: CataloguePlan[], interval: string): CataloguePlan[] {
  return [
    ...selfServePlans(plans).filter((plan) => plan.billing_interval === interval),
    ...plans.filter((plan) => plan.contact_sales),
  ];
}

export function popularPlanCode(plans: CataloguePlan[]): string | null {
  // A sales-led plan is never the one to steer a visitor towards.
  const paid = selfServePlans(plans).filter((plan) => plan.price_minor > 0);
  if (paid.length < 3) return null;
  return paid[Math.floor((paid.length - 1) / 2)].code;
}

/**
 * The cadence a marketing page opens on: the one the platform owner leads
 * with, or the shortest cycle on sale when they sell nothing on it.
 *
 * Ignoring a cadence with no plans behind it is what stops the pricing page
 * opening on an empty tab the moment the last quarterly plan is withdrawn.
 */
export function leadingInterval(plans: CataloguePlan[], preferred?: string | null): string {
  const intervals = billingIntervalsOf(selfServePlans(plans));
  if (preferred && intervals.includes(preferred)) return preferred;
  return intervals[0] ?? 'MONTHLY';
}
